import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from 'react-auth-kit';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { confirmBulkDelete, getPendingBulkDelete, PendingBulkDeleteResponse } from 'src/api/functions/assetsApi';
import { ErrorResponseResult } from 'src/types/TApiQueries';
import { Button } from 'src/components';
import styles from './BulkDeleteConfirm.module.scss';

// Icons as inline SVG components
const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const StorageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <ellipse cx="12" cy="5" rx="9" ry="3"/>
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
  </svg>
);

const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

type PageState = 'loading' | 'ready' | 'confirming' | 'success' | 'error';

interface ConfirmResult {
  job_id?: string;
  message?: string;
}

const formatDate = (dateString: string): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleString();
};

const formatDateShort = (dateString: string): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const BulkDeleteConfirm: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const accessToken = useAccessToken();
  
  const [pageState, setPageState] = useState<PageState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [pendingData, setPendingData] = useState<PendingBulkDeleteResponse | null>(null);
  const hasFetchedRef = useRef(false);

  // Extract token from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  useEffect(() => {
    // Check authentication status
    if (!isAuthenticated()) {
      // Redirect to login with return URL
      const returnUrl = `/bulk-purge-assets/confirm?token=${token}`;
      history.replace(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // Fetch pending deletion details only once
    if (token && accessToken && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPendingDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, accessToken]);

  const fetchPendingDetails = async () => {
    if (!token || !accessToken) return;
    
    setPageState('loading');
    setError(null);

    try {
      const response = await getPendingBulkDelete(token, accessToken);
      
      // Check if it's an error response
      if ('status' in response && typeof (response as ErrorResponseResult).status === 'number') {
        const errorResponse = response as ErrorResponseResult;
        throw new Error(errorResponse.message || errorResponse.error || 'Failed to fetch bulk purge assets details');
      }

      // Response is the data directly
      const data = response as PendingBulkDeleteResponse;
      
      // Check if we got valid data
      if (!data || data.asset_count === undefined) {
        throw new Error('Invalid response from server');
      }

      setPendingData(data);
      setPageState('ready');
    } catch (err: any) {
      console.error('Error fetching pending details:', err);
      setError(err.message || 'Failed to load bulk purge assets request details');
      setPageState('error');
      hasFetchedRef.current = false; // Allow retry
    }
  };

  // If no token, show error
  if (!token) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={`${styles.iconContainer} ${styles.iconError}`}>
                <ErrorIcon />
              </div>
              <h1 className={styles.title}>Invalid Request</h1>
              <p className={styles.subtitle}>
                No purge token was provided. Please use the link from your email.
              </p>
            </div>
            <div className={styles.buttonContainer}>
              <Button mode="tertiary" onClick={() => history.push('/assets')}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleConfirm = async () => {
    if (!token || !accessToken) return;
    
    setPageState('confirming');
    setError(null);

    try {
      const response = await confirmBulkDelete(token, accessToken);
      
      // Check if it's an error response
      if ('status' in response && typeof (response as ErrorResponseResult).status === 'number') {
        const errorResponse = response as ErrorResponseResult;
        throw new Error(errorResponse.message || errorResponse.error || 'Failed to confirm bulk purge assets');
      }

      // Response is the data directly
      const data = response as any;
      
      if (data.job_id) {
        setJobId(data.job_id);
        setPageState('success');
        
        // Auto-redirect to job page after 3 seconds
        setTimeout(() => {
          history.push(`/job/${data.job_id}`);
        }, 3000);
      } else {
        setPageState('success');
      }
    } catch (err: any) {
      console.error('Error confirming deletion:', err);
      setError(err.message || 'An unexpected error occurred');
      setPageState('ready'); // Go back to ready state so user can retry
    }
  };

  const handleCancel = () => {
    history.push('/assets');
  };

  // Loading state while fetching details
  if (pageState === 'loading') {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p className={styles.loadingText}>Loading bulk purge assets request details...</p>
      </div>
    );
  }

  // Loading state while confirming
  if (pageState === 'confirming') {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p className={styles.loadingText}>Processing bulk purge assets request...</p>
      </div>
    );
  }

  // Error state when fetching failed
  if (pageState === 'error') {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={`${styles.iconContainer} ${styles.iconError}`}>
                <ErrorIcon />
              </div>
              <h1 className={styles.title}>Unable to Load Request</h1>
              <p className={styles.subtitle}>
                {error || 'Failed to load the bulk purge assets request details.'}
              </p>
            </div>
            <div className={styles.buttonContainer}>
              <Button mode="primary" onClick={fetchPendingDetails}>
                Retry
              </Button>
              <Button mode="tertiary" onClick={() => history.push('/assets')}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={`${styles.iconContainer} ${styles.iconSuccess}`}>
                <CheckIcon />
              </div>
              <h1 className={styles.title}>Bulk Purge Assets Confirmed</h1>
              <p className={styles.subtitle}>
                Your bulk purge assets request has been confirmed and is now being processed.
              </p>
            </div>
            {jobId && (
              <div className={styles.successContainer}>
                <div className={styles.jobInfo}>
                  <div className={styles.jobLabel}>Job ID</div>
                  <div className={styles.jobId}>{jobId}</div>
                </div>
                <a href={`/job/${jobId}`} className={styles.linkButton}>
                  View Job Status
                  <ArrowRightIcon />
                </a>
                <p className={styles.redirectText}>Redirecting automatically in 3 seconds...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Ready state - show confirmation dialog with details
  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={`${styles.iconContainer} ${styles.iconWarning}`}>
              <WarningIcon />
            </div>
            <h1 className={styles.title}>Confirm Bulk Purge Assets</h1>
            <p className={styles.subtitle}>
              Review the details below before confirming the bulk purge assets.
            </p>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {/* Deletion Details */}
          {pendingData && (
            <div className={styles.detailsSection}>
              {/* Asset Count - Highlighted */}
              <div className={styles.assetCountBox}>
                <div className={styles.assetCountNumber}>
                  {pendingData.asset_count?.toLocaleString() || 0}
                </div>
                <div className={styles.assetCountLabel}>assets will be purged</div>
              </div>

              {/* Details Grid */}
              <div className={styles.detailsGrid}>
                {/* Time Range */}
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}>
                    <CalendarIcon />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>Time Range</div>
                    <div className={styles.detailValue}>
                      {formatDateShort(pendingData.start_time)} — {formatDateShort(pendingData.end_time)}
                    </div>
                  </div>
                </div>

                {/* Storage Deletion */}
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}>
                    <StorageIcon />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>Storage Deletion</div>
                    <div className={styles.detailValue}>
                      <span className={pendingData.storage_deletion?.enabled ? styles.statusEnabled : styles.statusDisabled}>
                        {pendingData.storage_deletion?.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Retention Filters */}
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}>
                    <FilterIcon />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>Retention Filters</div>
                    <div className={styles.detailValue}>
                      {pendingData.retention_filters_applied 
                        ? `${pendingData.retention_filters?.length || 0} filter(s) applied`
                        : 'None'}
                    </div>
                  </div>
                </div>

                {/* Expiration */}
                <div className={styles.detailItem}>
                  <div className={styles.detailIcon}>
                    <ClockIcon />
                  </div>
                  <div className={styles.detailContent}>
                    <div className={styles.detailLabel}>Expires At</div>
                    <div className={styles.detailValue}>
                      {formatDate(pendingData.expires_at)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Storage Note */}
              {pendingData.storage_deletion?.note && (
                <div className={styles.storageNote}>
                  <StorageIcon />
                  <span>{pendingData.storage_deletion.note}</span>
                </div>
              )}

              {/* Requested By */}
              <div className={styles.requestedBy}>
                Requested by: <strong>{pendingData.user_email}</strong>
              </div>
            </div>
          )}

          <div className={styles.warningBox}>
            <div className={styles.warningTitle}>
              <TrashIcon />
              This action cannot be undone
            </div>
            <p className={styles.warningText}>
              Once confirmed, the selected assets will be permanently purged. 
              Any links or references to these assets will no longer work.
            </p>
          </div>

          <div className={styles.buttonContainer}>
            <Button 
              mode="decline"
              onClick={handleConfirm}
            >
              Confirm Bulk Purge Assets
            </Button>
            <Button 
              mode="tertiary"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteConfirm;
